import React, { FC, useEffect, useState } from 'react';
import { Box, Divider, LinearProgress, Link, Typography, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import ScoreService from '../../services/ScoreService';
import { WidgetNoData } from './WidgetNoData';
import { IPlatform } from '../../shared/interfaces/IPlatform';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';
import PlatformService from '../../services/PlatformService';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import GMBService from '../../services/GMBService';
import { IGMBLocationsResponse } from '../../shared/interfaces/IGMBLocationsResponse';
import GrowthIndicator from '../GrowthIndicator/GrowthIndicator';
import { constants } from '../../constants/constants';
import StarRating from '../StarRating/StarRating';
import PlatformImage from '../Logo/PlatformImage';

interface ScoreChange {
  platform: string;
  platformName: string;
  company: string;
  label: string;
  oldScore: number;
  newScore: number;
  change: number;
}

const WidgetScoreChange: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [scoreChanges, setScoreChanges] = useState<ScoreChange[]>([]);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };

    const getMonitoring = async () => {
      let monitoringEntries: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        false,
        'both',
        setLoading,
        controller.signal
      );

      if (!monitoringEntries) return;

      const gmbMonitoredLocations: IGMBLocationsResponse = await GMBService.getAllLocations(
        undefined,
        1000,
        setLoading,
        controller.signal,
        true
      );

      if (!gmbMonitoredLocations) return;

      const hasGmbEntries = gmbMonitoredLocations.locations.length > 0;

      for (const location of gmbMonitoredLocations.locations) {
        const mon: IMonitoring = {
          company: location.locationID,
          createdAt: '',
          deletedAt: '',
          id: location.ID,
          jobs: [],
          platformId: -2,
          label: location.locationData.title,
          url: '',
          updatedAt: '',
          communityManagement: location.communityManagement,
          isBenchmark: false,
          type: 'LOCATION',
        };
        monitoringEntries.push(mon);
      }

      if (hasGmbEntries) {
        setPlatforms((prevState) => [...prevState, PlatformService.getPseudoGoogle()]);
      }

      setMonitoring(monitoringEntries);
      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitoringEntries.find((m) => m.platformId === v.id));
      });
    };

    (async () => {
      await getPlatforms();
      await getMonitoring();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const getScoreChanges = async (monitoring: IMonitoring[]) => {
      const changes: ScoreChange[] = [];

      for (const value of monitoring) {
        const platformName = PlatformService.getPlatformName(value.platformId, platforms);

        // Get current scores
        const currentScores = await ScoreService.getAllScores(
          platformName,
          value.company,
          'total',
          setLoading,
          controller.signal
        );

        if (!Array.isArray(currentScores) || currentScores.length === 0) continue;

        // Get the most recent score
        const latestScore = currentScores[0];

        // Find a score from approximately 30 days ago
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        let oldScoreEntry = null;
        for (const score of currentScores) {
          const scoreDate = new Date(score.scrapedAt);
          if (scoreDate <= thirtyDaysAgo) {
            oldScoreEntry = score;
            break;
          }
        }

        // If we found a score from 30 days ago and there's a change
        if (oldScoreEntry && Math.abs(latestScore.score.score - oldScoreEntry.score.score) > 0.01) {
          changes.push({
            platform: platformName,
            platformName: platformName,
            company: value.company,
            label: value.label,
            oldScore: oldScoreEntry.score.score,
            newScore: latestScore.score.score,
            change: latestScore.score.score - oldScoreEntry.score.score,
          });
        }
      }

      setScoreChanges(changes);
      setLoading(false);
    };

    if (monitoring.length > 0 && platforms.length > 0) {
      (async () => await getScoreChanges(monitoring))();
    }

    return () => {
      controller.abort();
    };
  }, [monitoring, platforms]);

  const getChangeText = (change: ScoreChange) => {
    const oldScore = change.oldScore.toFixed(1);
    const newScore = change.newScore.toFixed(1);

    if (change.change > 0) {
      return (
        <Trans
          i18nKey="score_increased"
          components={{ b: <b /> }}
          values={{ oldScore, newScore, label: change.label }}
        ></Trans>
      );
    } else {
      return (
        <Trans
          i18nKey="score_decreased"
          components={{ b: <b /> }}
          values={{ oldScore, newScore, label: change.label }}
        ></Trans>
      );
    }
  };

  return (
    <Box
      sx={{
        maxHeight: '290px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <Box>
          {scoreChanges.length > 0 ? (
            scoreChanges.map((change, index) => {
              const matchingMonitoring = monitoring.find((mon) => mon.company === change.company);
              return (
                <Box key={`${change.platform}-${change.company}`}>
                  <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(1) }} />
                  <Box sx={{ display: 'flex', flexDirection: 'row', p: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexGrow: 1,
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ width: '90px' }}>
                          <PlatformImage
                            alt={t('platform_logo')}
                            src={constants.platform_logos[change.platform]}
                          />
                        </Box>
                        {matchingMonitoring && (
                          <Box
                            sx={{
                              width: '200px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            <Link
                              href={
                                change.platform === 'googleplace'
                                  ? `https://www.google.com/maps/place/?q=place_id:${matchingMonitoring.company}`
                                  : matchingMonitoring.url
                              }
                              variant="body2"
                              underline={'none'}
                              target="_blank"
                              sx={{
                                cursor: 'pointer',
                                color:
                                  theme.palette.mode === 'dark'
                                    ? 'white'
                                    : theme.palette.primary.main,
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                textDecoration: 'none',
                              }}
                            >
                              {matchingMonitoring.label}
                            </Link>
                          </Box>
                        )}
                        <Box sx={{ width: '120px' }}>
                          <StarRating value={change.newScore} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2">{getChangeText(change)}</Typography>
                        </Box>
                        <Box>
                          <GrowthIndicator growth={change.change} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })
          ) : (
            <>
              <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <WidgetNoData />
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WidgetScoreChange;
